import type { HoldingReport } from '@interfaces/holdingReport';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { SubAccountReport } from '@interfaces/subAccountReport';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { TFunction } from 'i18next';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import { getReportMarketValueCents } from '../presentation/reportLabels';
import {
  buildReportDisplayMetadata,
  type ReportDisplayMetadata,
} from '../snapshot/buildReportDisplayMetadata';
import {
  formatValidationErrorMessages,
  parseValidationErrorCodes,
} from './formatValidationErrorMessage';

const MIN_VIABLE_MARKET_VALUE_CENTS = 100;

const formatShareQuantity = (quantity: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 4,
  }).format(quantity);

const getSubAccountLabel = (
  subAccountReport: SubAccountReport,
  metadata: ReportDisplayMetadata,
  t: TFunction,
): string => {
  const subAccountId = subAccountReport.subAccount?.id;

  if (subAccountId) {
    const known = metadata.subAccountsById.get(subAccountId);

    if (known?.name) {
      return known.name;
    }
  }

  const accountType = formatAuditAccountType(
    subAccountReport.subAccount?.account?.type,
  );

  if (accountType && accountType !== '—') {
    return accountType;
  }

  return t('validationDiagnostics.account', { ns: 'bulkRebalance' });
};

const appendNegativeHoldingDiagnostics = (
  holding: HoldingReport,
  accountLabel: string | undefined,
  messages: string[],
  t: TFunction,
) => {
  const ticker =
    holding.financialProduct?.ticker ||
    holding.ticker ||
    t('validationDiagnostics.unknownTicker', { ns: 'bulkRebalance' });
  const prefix = accountLabel ? `${accountLabel}: ` : '';

  if (holding.quantity < 0) {
    messages.push(
      t('validationDiagnostics.negativeHoldingQuantity', {
        ns: 'bulkRebalance',
        accountPrefix: prefix,
        ticker,
        quantity: formatShareQuantity(holding.quantity),
        value: formatMoneyValue(holding.valueCents),
      }),
    );
    return;
  }

  if ((holding.valueCents ?? 0) < 0) {
    messages.push(
      t('validationDiagnostics.negativeHoldingValue', {
        ns: 'bulkRebalance',
        accountPrefix: prefix,
        ticker,
        value: formatMoneyValue(holding.valueCents),
      }),
    );
  }
};

export const buildPortfolioValidationDiagnostics = (
  translateBackendString: (translatedObject: unknown) => string,
  report: PortfolioReport | undefined,
  validationErrors: string[],
  t: TFunction,
): string[] => {
  if (!report) {
    return [];
  }

  const metadata = buildReportDisplayMetadata(
    translateBackendString,
    report,
    t,
  );
  const messages: string[] = [];
  const codes = new Set(parseValidationErrorCodes(validationErrors));

  if (report.cashCents < 0) {
    messages.push(
      t('validationDiagnostics.negativePortfolioCash', {
        ns: 'bulkRebalance',
        value: formatMoneyValue(report.cashCents),
      }),
    );
  }

  for (const subAccountReport of report.subAccounts ?? []) {
    const accountLabel = getSubAccountLabel(subAccountReport, metadata, t);

    if ((subAccountReport.cashCents ?? 0) < 0) {
      messages.push(
        t('validationDiagnostics.negativeAccountCash', {
          ns: 'bulkRebalance',
          account: accountLabel,
          value: formatMoneyValue(subAccountReport.cashCents),
        }),
      );
    }

    const subAccountMarketValueCents = subAccountReport.marketValueCents ?? 0;

    if (
      subAccountMarketValueCents < MIN_VIABLE_MARKET_VALUE_CENTS &&
      (codes.has('NON_POSITIVE_MARKET_VALUE') || subAccountMarketValueCents < 0)
    ) {
      messages.push(
        t('validationDiagnostics.accountMarketValue', {
          ns: 'bulkRebalance',
          account: accountLabel,
          value: formatMoneyValue(subAccountMarketValueCents),
        }),
      );
    }

    for (const holding of subAccountReport.holdings ?? []) {
      appendNegativeHoldingDiagnostics(holding, accountLabel, messages, t);
    }
  }

  if ((report.subAccounts ?? []).length === 0) {
    for (const holding of report.holdings ?? []) {
      appendNegativeHoldingDiagnostics(holding, undefined, messages, t);
    }
  }

  const portfolioMarketValueCents = getReportMarketValueCents(report);

  if (
    codes.has('NON_POSITIVE_MARKET_VALUE') &&
    portfolioMarketValueCents < MIN_VIABLE_MARKET_VALUE_CENTS
  ) {
    messages.push(
      t('validationDiagnostics.belowMinimumPortfolioMarketValue', {
        ns: 'bulkRebalance',
        value: formatMoneyValue(portfolioMarketValueCents),
      }),
    );
  }

  return [...new Set(messages)];
};

export const resolveValidationDisplayMessages = (
  translateBackendString: (translatedObject: unknown) => string,
  report: PortfolioReport | undefined,
  validationErrors: string[],
  t: TFunction,
): string[] => {
  const diagnostics = buildPortfolioValidationDiagnostics(
    translateBackendString,
    report,
    validationErrors,
    t,
  );

  if (diagnostics.length > 0) {
    return [...new Set(diagnostics)];
  }

  return [...new Set(formatValidationErrorMessages(validationErrors, t))];
};
